
import React, { useState, useEffect, useRef, useMemo } from 'react';
// Added missing Mic import from lucide-react
import { Sparkles, BrainCircuit, Activity, Target, ClipboardList, Loader2, CheckCircle2, ShieldAlert, AlertTriangle, Save, FileText, RotateCcw, Mic, AlertCircle } from 'lucide-react';
import { Patient, Approach, AudioAnalysis } from '../types';
import { analyzeSessionText, registerAiConsent, transcribeAudio } from '../services/aiApi';
import { getRecordingPlaybackUrl } from '../services/recordingsApi';

interface AIAnalysisProps {
  patients: Patient[];
  recordings: any[];
  onSaveToPatient: (patientId: string, recordingId: string, analysis: AudioAnalysis, attachToProntuario?: boolean) => void;
  initialRecordingId?: string | null;
}

const APPROACH_METADATA: Record<string, { color: string, sigla: string }> = {
  [Approach.TCC]: { color: 'bg-blue-600', sigla: 'TC' },
  [Approach.ACT]: { color: 'bg-indigo-600', sigla: 'AC' },
  [Approach.DBT]: { color: 'bg-cyan-600', sigla: 'DB' },
  [Approach.FREUD]: { color: 'bg-purple-600', sigla: 'PF' },
  [Approach.JUNG]: { color: 'bg-fuchsia-600', sigla: 'JA' },
  [Approach.PSICODINAMICA]: { color: 'bg-violet-600', sigla: 'PB' },
  [Approach.WINNICOTT]: { color: 'bg-pink-600', sigla: 'PW' },
  [Approach.LACAN]: { color: 'bg-rose-600', sigla: 'PL' },
  [Approach.ROGERS]: { color: 'bg-emerald-600', sigla: 'CP' },
  [Approach.GESTALT]: { color: 'bg-teal-600', sigla: 'GT' },
  [Approach.LOGOTERAPIA]: { color: 'bg-green-600', sigla: 'LT' },
  [Approach.MINUCHIN]: { color: 'bg-orange-600', sigla: 'FE' },
  [Approach.SISTEMICA]: { color: 'bg-amber-600', sigla: 'SB' },
  [Approach.NARRATIVA]: { color: 'bg-yellow-600', sigla: 'TN' },
  [Approach.CASAL]: { color: 'bg-red-600', sigla: 'TC' },
  [Approach.TBS]: { color: 'bg-lime-600', sigla: 'TS' },
  [Approach.ESTRATEGICA]: { color: 'bg-sky-600', sigla: 'TE' },
  [Approach.ABA]: { color: 'bg-emerald-700', sigla: 'AB' },
  [Approach.HIPNOSE]: { color: 'bg-indigo-800', sigla: 'HP' },
};

const ANALYSIS_MESSAGES = [
  "Iniciando motor de análise...",
  "Convertendo áudio para processamento...",
  "Realizando transcrição literal...",
  "Identificando padrões verbais...",
  "Cruzando com a abordagem escolhida...",
  "Mapeando fenômenos transferenciais...",
  "Finalizando síntese clínica..."
];

const MIN_TRANSCRIPT_CHARS_FOR_ANALYSIS = 20;

const AIAnalysis: React.FC<AIAnalysisProps> = ({ patients = [], recordings = [], onSaveToPatient, initialRecordingId }) => {
  const [step, setStep] = useState(() => initialRecordingId ? 2 : 1);
  const [selectedRecordingId, setSelectedRecordingId] = useState<string | null>(initialRecordingId || null);
  const [selectedApproach, setSelectedApproach] = useState<Approach | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState(ANALYSIS_MESSAGES[0]);
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [editedTranscription, setEditedTranscription] = useState('');
  const [editedSessionSynthesis, setEditedSessionSynthesis] = useState('');
  const [editedStrategicPlan, setEditedStrategicPlan] = useState('');

  const progressTimer = useRef<number | null>(null);
  const messageTimer = useRef<number | null>(null);

  useEffect(() => {
    if (initialRecordingId) {
      setSelectedRecordingId(initialRecordingId);
      setStep(2);
    }
  }, [initialRecordingId]);

  useEffect(() => {
    if (analysisResult) {
      setEditedTranscription(analysisResult.transcription || '');
      setEditedSessionSynthesis(analysisResult.sessionSynthesis || '');
      setEditedStrategicPlan(analysisResult.strategicPlan || '');
    } else {
      setEditedTranscription('');
      setEditedSessionSynthesis('');
      setEditedStrategicPlan('');
    }
  }, [analysisResult]);

  const selectedRecording = recordings.find(r => r.id === selectedRecordingId);
  const isUuid = (value?: string): boolean =>
    !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
  
  // Encontra o paciente real ou cria um objeto virtual apenas para exibição.
  const selectedPatient = useMemo(() => {
    const real = patients.find(p => p.id === selectedRecording?.patientId);
    if (real) return real;
    if (!selectedRecording) return null;

    return {
      id: 'external',
      name: selectedRecording.patientName || 'Paciente Externo',
      activeApproach: selectedApproach || Approach.TCC,
      cycleFrequency: 'Mensal' as any,
      selectedIndicatorIds: [],
      cycles: [],
      createdAt: new Date().toISOString(),
      mainComplaint: 'Não especificada'
    } as Patient;
  }, [patients, selectedRecording, selectedApproach]);

  useEffect(() => {
    return () => {
      if (progressTimer.current) clearInterval(progressTimer.current);
      if (messageTimer.current) clearInterval(messageTimer.current);
    };
  }, []);

  useEffect(() => {
    if (isAnalyzing) {
      setAnalysisProgress(5);
      progressTimer.current = window.setInterval(() => {
        setAnalysisProgress(prev => prev < 95 ? prev + 0.5 : prev);
      }, 300);

      let msgIdx = 0;
      messageTimer.current = window.setInterval(() => {
        msgIdx = (msgIdx + 1) % ANALYSIS_MESSAGES.length;
        setStatusMessage(ANALYSIS_MESSAGES[msgIdx]);
      }, 3000);
    }
  }, [isAnalyzing]);

  const audioUrlToFile = async (url: string, fallbackName: string): Promise<File> => {
    try {
      const response = await fetch(url).catch(err => {
        if (url.startsWith('blob:')) {
          throw new Error("O acesso ao áudio expirou. Isso acontece se a página foi recarregada. Por favor, faça o upload do arquivo novamente na aba Gravações.");
        }
        throw new Error("Erro de conexão ao carregar o áudio. Verifique se o arquivo ainda existe.");
      });

      if (!response.ok) throw new Error("Não foi possível acessar o arquivo de áudio.");
      const blob = await response.blob();
      const name = fallbackName?.trim() || 'audio.webm';
      return new File([blob], name, { type: blob.type || 'audio/webm' });
    } catch (e: any) {
      throw new Error(e.message || "Não foi possível acessar o arquivo de áudio.");
    }
  };

  const handleStartAnalysis = async () => {
    setError(null);
    if (!selectedRecording) return setError("Grave ou envie um áudio primeiro.");
    if (!selectedApproach) return setError("Escolha o Agente Supervisor.");
    if (!isUuid(selectedRecording.patientId)) {
      return setError("Vincule a gravação a um paciente cadastrado para analisar com OpenAI.");
    }
    const linkedPatient = patients.find(p => p.id === selectedRecording.patientId);
    if (!linkedPatient) {
      return setError("O paciente vinculado a esta gravação não existe mais nesta conta. Refaça o vínculo na aba Gravações.");
    }
    
    setIsAnalyzing(true);
    try {
      console.info('[AIAnalysis] start', {
        recordingId: selectedRecording.id,
        recordingName: selectedRecording.name,
        patientId: selectedRecording.patientId,
        approach: selectedApproach,
      });
      const audioSourceUrl = selectedRecording.url || (await getRecordingPlaybackUrl(selectedRecording.id)).url;
      const audioFile = await audioUrlToFile(audioSourceUrl, selectedRecording.name);
      console.info('[AIAnalysis] audio-ready', {
        name: audioFile.name,
        type: audioFile.type,
        size: audioFile.size,
      });
      await registerAiConsent(selectedRecording.patientId);
      console.info('[AIAnalysis] consent-ok');
      const transcription = await transcribeAudio(selectedRecording.patientId, audioFile, selectedRecording.id);
      const transcriptText = transcription.text;
      console.info('[AIAnalysis] transcript-ok', { chars: transcriptText.length });
      if (transcriptText.trim().length < MIN_TRANSCRIPT_CHARS_FOR_ANALYSIS) {
        throw new Error("A transcrição ficou curta demais para análise clínica. Grave ou envie um áudio com mais fala audível e tente novamente.");
      }
      const analysis = await analyzeSessionText(
        selectedRecording.patientId,
        transcriptText,
        selectedApproach,
        undefined,
        transcription.transcriptionId,
      );
      console.info('[AIAnalysis] analyze-session-ok', {
        readingChars: analysis.theoreticalReading?.length,
        management: analysis.technicalManagement?.length,
      });
      // Matriz de Responsabilidade (ANÁLISE IA): panorama factual como perfil,
      // leitura teórica como síntese e manejo + riscos como plano.
      const ov = analysis.sessionOverview;
      const abordagem = analysis.approach || selectedApproach;
      const result = {
        transcription: transcriptText,
        profileSummary: [
          `Queixa: ${ov.complaint}`,
          `Enquadre/setting: ${ov.setting}`,
          `Clima relacional: ${ov.relationalClimate}`,
          ov.contextGaps.length ? `Lacunas contextuais: ${ov.contextGaps.join('; ')}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
        sessionSynthesis: `LEITURA TEÓRICA (${abordagem}):\n${analysis.theoreticalReading}`,
        strategicPlan: [
          `MANEJO TÉCNICO (${abordagem}):`,
          ...analysis.technicalManagement.map((t, i) => `${i + 1}. ${t}`),
          '',
          `PONTOS DE ATENÇÃO E RISCO (${abordagem}):`,
          ...analysis.attentionAndRisk.map((t) => `⚠ ${t}`),
        ].join('\n'),
        indicatorScores: {},
      };
      
      if (!result || typeof result !== 'object' || (!result.transcription && !result.sessionSynthesis)) {
        throw new Error("A IA não conseguiu gerar uma análise válida para este áudio. Verifique a qualidade do som.");
      }

      setAnalysisProgress(100);
      setTimeout(() => {
        setAnalysisResult(result);
        setStep(3);
        setIsAnalyzing(false);
      }, 500);
    } catch (err: any) {
      console.error("Erro na análise IA:", err);
      setError(err.message || "Falha crítica no processamento. Tente novamente ou verifique a conexão.");
      setIsAnalyzing(false);
    }
  };

  const handleSaveResult = (attachToProntuario: boolean = false) => {
    if (!selectedPatient || !selectedRecording || !analysisResult || !selectedApproach) return;

    setIsSaving(true);
    const analysisToSave: AudioAnalysis = {
      id: Date.now().toString(),
      date: new Date().toLocaleDateString('pt-BR'),
      recordingName: selectedRecording.name,
      approach: selectedApproach,
      transcription: editedTranscription || "Transcrição não disponível",
      sessionSynthesis: editedSessionSynthesis || "Síntese não gerada",
      strategicPlan: editedStrategicPlan || "Plano não disponível",
      profileSummary: analysisResult.profileSummary || "Sem perfil",
      indicatorScores: analysisResult.indicatorScores || {}
    };

    onSaveToPatient(selectedPatient.id, selectedRecording.id, analysisToSave, attachToProntuario);
    
    setTimeout(() => {
      setIsSaving(false);
      setStep(1);
      setAnalysisResult(null);
    }, 500);
  };

  const getApproachMeta = (app: string) => {
    return APPROACH_METADATA[app] || { color: 'bg-slate-600', sigla: 'IA' };
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 md:space-y-8 animate-in fade-in duration-500 pb-12 md:pb-20">
      {/* Header Contextual */}
      {step < 3 && (
        <div className="bg-indigo-900 rounded-[2rem] md:rounded-[2.5rem] p-5 md:p-8 text-white flex justify-between items-center shadow-xl relative overflow-hidden">
           <div className="flex items-center gap-4 relative z-10">
              <div className="p-4 bg-white/10 rounded-3xl"><Sparkles size={32}/></div>
              <div>
                 <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight">Supervisão Clínica IA</h2>
                 <p className="text-indigo-200 text-[10px] font-bold uppercase tracking-widest mt-1">Processamento de Áudio e Transcrição</p>
              </div>
           </div>
           <BrainCircuit className="absolute -right-10 -bottom-10 text-white/5 opacity-10" size={200} />
        </div>
      )}

      {/* Erros - Visíveis em qualquer etapa se houver falha */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 border-2 border-red-100 dark:border-red-900/30 p-6 rounded-[2rem] flex items-center gap-4 text-red-600 dark:text-red-400 animate-in slide-in-from-top-4">
          <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-2xl"><AlertTriangle size={24} /></div>
          <div className="flex-1">
            <p className="text-sm font-black uppercase tracking-widest">Ops! Algo deu errado</p>
            <p className="text-xs font-bold opacity-70">{error}</p>
          </div>
          <button onClick={() => { setError(null); setStep(1); }} className="p-2 bg-white dark:bg-slate-800 rounded-xl shadow-sm"><RotateCcw size={18} /></button>
        </div>
      )}

      {/* Step 1: Seleção de Gravação */}
      {step === 1 && (
        <div className="space-y-6">
          <h3 className="text-xl font-black dark:text-white px-4">1. Selecionar Gravação de Sessão</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recordings.filter(r => r.url || r.status === 'READY').map(rec => (
              <button 
                key={rec.id}
                onClick={() => setSelectedRecordingId(rec.id)}
                className={`p-6 rounded-[2rem] border-2 text-left transition-all group ${selectedRecordingId === rec.id ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-indigo-200'}`}
              >
                <div className="flex justify-between items-start mb-2">
                   <div className="flex items-center gap-2 truncate pr-2">
                      <Mic size={14} className="text-indigo-500 shrink-0" />
                      <p className="text-sm font-black dark:text-white truncate">{rec.name}</p>
                   </div>
                   <CheckCircle2 size={16} className={rec.transcription ? "text-emerald-500" : "text-slate-200"} />
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <p className="text-[10px] text-slate-400 font-bold uppercase">{rec.patientName} • {rec.date}</p>
                  {!rec.patientId && <span title="Paciente não vinculado" className="inline-flex"><AlertCircle size={10} className="text-amber-500" /></span>}
                </div>
              </button>
            ))}
          </div>
          {recordings.filter(r => r.url || r.status === 'READY').length === 0 && (
            <div className="p-5 sm:p-10 md:p-20 text-center border-2 border-dashed rounded-[2rem] md:rounded-[3rem] border-slate-200 dark:border-slate-800">
               <Mic size={48} className="mx-auto mb-4 text-slate-300" />
               <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Nenhuma gravação de áudio encontrada.</p>
               <p className="text-xs text-slate-400 mt-2">Grave uma sessão ou faça upload na aba de Gravações.</p>
            </div>
          )}
          <button disabled={!selectedRecordingId} onClick={() => setStep(2)} className="w-full p-4 md:p-6 bg-indigo-600 text-white rounded-[1.5rem] font-black uppercase shadow-xl hover:bg-indigo-700 disabled:opacity-50 transition-all active:scale-[0.98]">Configurar Análise</button>
        </div>
      )}

      {/* Step 2: Seleção de Abordagem / Supervisor */}
      {step === 2 && (
        <div className="space-y-6">
          <h3 className="text-xl font-black dark:text-white px-4">2. Agente Supervisor Especialista</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.values(Approach).map(app => {
              const meta = getApproachMeta(app);
              const isSel = selectedApproach === app;
              return (
                <button key={app} onClick={() => setSelectedApproach(app)} className={`p-6 rounded-[2rem] border-2 text-left transition-all ${isSel ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 ring-4 ring-indigo-500/10 shadow-lg' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800'}`}>
                  <div className={`${meta.color} w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-xs mb-4 shadow-md`}>{meta.sigla}</div>
                  <h4 className="text-[10px] font-black dark:text-white uppercase leading-tight">{app}</h4>
                </button>
              );
            })}
          </div>
          <div className="pt-6">
            {!isAnalyzing ? (
              <div className="flex gap-4">
                <button onClick={() => setStep(1)} className="flex-1 p-6 bg-slate-100 dark:bg-slate-800 rounded-[1.5rem] font-black uppercase text-xs transition-all hover:bg-slate-200 dark:text-white">Voltar</button>
                <button disabled={!selectedApproach} onClick={handleStartAnalysis} className="flex-[2] p-6 bg-emerald-600 text-white rounded-[1.5rem] font-black uppercase shadow-xl hover:bg-emerald-700 disabled:opacity-50 transition-all active:scale-[0.98]">Ouvir e Analisar com IA</button>
              </div>
            ) : (
              <div className="bg-white dark:bg-slate-900 p-6 md:p-12 rounded-[2rem] md:rounded-[3rem] border dark:border-slate-800 shadow-2xl text-center animate-in zoom-in-95">
                <Loader2 className="animate-spin text-indigo-600 mx-auto mb-6" size={48} />
                <h4 className="text-xl font-black dark:text-white mb-2">{statusMessage}</h4>
                <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mt-6">
                  <div className="h-full bg-indigo-600 transition-all duration-300" style={{ width: `${analysisProgress}%` }} />
                </div>
                <p className="text-[9px] text-slate-400 font-black uppercase tracking-[0.2em] mt-6">A IA está processando o arquivo de áudio...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Resultados (Fallback para evitar tela em branco) */}
      {step === 3 && (
        analysisResult ? (
          <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
             <div className="flex flex-col md:flex-row justify-between items-center bg-white dark:bg-slate-900 p-6 rounded-[2rem] border dark:border-slate-800 shadow-sm gap-4">
                <div className="flex items-center gap-4">
                   <div className={`${getApproachMeta(selectedApproach || '').color} p-3 rounded-2xl text-white font-black`}>
                      {getApproachMeta(selectedApproach || '').sigla}
                   </div>
                   <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-black dark:text-white text-lg">{selectedPatient?.name || "Paciente Selecionado"}</h3>
                        {selectedPatient?.id === 'external' && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[8px] font-black uppercase rounded-full">Não Vinculado</span>}
                      </div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Supervisor: {selectedApproach}</p>
                   </div>
                </div>
                <div className="flex gap-3">
                   <button
                    onClick={() => handleSaveResult(false)}
                    disabled={isSaving || selectedPatient?.id === 'external'}
                    className={`flex items-center gap-2 px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl active:scale-95 ${selectedPatient?.id === 'external' ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-200'}`}
                    title={selectedPatient?.id === 'external' ? "Vincule um paciente na aba Gravações para salvar" : "Salvar análise"}
                   >
                      {isSaving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14}/> {selectedPatient?.id === 'external' ? 'Vincule um Paciente para Salvar' : 'Salvar em Áudios e transcrições'}</>}
                   </button>
                   <button onClick={() => { setStep(1); setAnalysisResult(null); }} className="px-6 py-3 bg-slate-100 dark:bg-slate-800 rounded-2xl text-[10px] font-black uppercase dark:text-white hover:bg-slate-200">Nova Análise</button>
                   <button
                     onClick={() => handleSaveResult(true)}
                     disabled={isSaving || selectedPatient?.id === 'external'}
                     className={`flex items-center gap-2 px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl active:scale-95 ${selectedPatient?.id === 'external' ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none' : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-200'}`}
                     title={selectedPatient?.id === 'external' ? "Vincule um paciente na aba Gravações para anexar ao prontuário" : "Salvar e Anexar ao Prontuário"}
                   >
                      {isSaving ? <Loader2 className="animate-spin" size={14} /> : <><FileText size={14}/> Anexar ao Prontuário</>}
                   </button>
                </div>
             </div>

             <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
                <div className="lg:col-span-2 space-y-6">
                   {/* Transcrição Literal */}
                   <div className="bg-white dark:bg-slate-900 p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border dark:border-slate-800 shadow-sm">
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-6 flex items-center gap-2"><FileText size={16}/> Transcrição Literal da Sessão</h4>
                      <textarea
                         id="ai-edited-transcription"
                         value={editedTranscription}
                         onChange={(e) => setEditedTranscription(e.target.value)}
                         className="w-full h-80 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-medium bg-slate-50 dark:bg-slate-800/50 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-y custom-scrollbar"
                         placeholder="Nenhuma transcrição disponível."
                       />
                   </div>

                   {/* Síntese Clínica */}
                   <div className="bg-white dark:bg-slate-900 p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border dark:border-slate-800 shadow-sm">
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400 mb-6 flex items-center gap-2"><ClipboardList size={16}/> Síntese e Fenômenos Clínicos</h4>
                      <textarea
                         id="ai-edited-synthesis"
                         value={editedSessionSynthesis}
                         onChange={(e) => setEditedSessionSynthesis(e.target.value)}
                         className="w-full h-64 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-medium bg-slate-50 dark:bg-slate-800/50 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y custom-scrollbar"
                         placeholder="Nenhuma síntese disponível."
                       />
                   </div>

                   {/* Plano Estratégico */}
                   <div className="bg-indigo-900 dark:bg-indigo-950 p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden">
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mb-6 flex items-center gap-2"><Target size={16}/> Plano Estratégico de Manejo</h4>
                      <textarea
                         id="ai-edited-strategic-plan"
                         value={editedStrategicPlan}
                         onChange={(e) => setEditedStrategicPlan(e.target.value)}
                         className="w-full h-64 text-sm leading-relaxed text-slate-100 bg-indigo-950/50 p-6 rounded-3xl border border-indigo-700/50 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-300 resize-y custom-scrollbar font-medium"
                         placeholder="Nenhum plano estratégico disponível."
                       />
                      <BrainCircuit className="absolute -right-20 -bottom-20 text-white/5 opacity-10 pointer-events-none" size={240} />
                   </div>
                </div>

                <div className="space-y-6">
                   <div className="bg-white dark:bg-slate-900 p-5 md:p-8 rounded-[2rem] md:rounded-[2.5rem] border dark:border-slate-800 shadow-sm">
                      <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Perfil Qualitativo</h4>
                      <p className="text-xs italic leading-relaxed text-slate-600 dark:text-slate-400 font-medium bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl border">"{analysisResult.profileSummary || "Sem resumo de perfil."}"</p>
                   </div>
                   
                   <div className="bg-amber-50 dark:bg-amber-900/10 p-6 rounded-3xl border border-amber-100 dark:border-amber-900/30 flex items-start gap-3">
                      <ShieldAlert size={18} className="text-amber-600 shrink-0" />
                      <p className="text-[9px] font-bold text-amber-800 dark:text-amber-200 leading-relaxed uppercase tracking-widest">Aviso: A transcrição e análise são geradas automaticamente. Verifique se o conteúdo reflete fielmente a sessão.</p>
                   </div>
                </div>
             </div>
          </div>
        ) : (
          <div className="p-5 sm:p-10 md:p-20 text-center bg-white dark:bg-slate-900 rounded-[2rem] md:rounded-[3rem] border border-dashed shadow-xl border-slate-200 dark:border-slate-800 flex flex-col items-center">
             <AlertTriangle size={48} className="text-amber-500 mb-6" />
             <h3 className="text-2xl font-black text-slate-800 dark:text-white uppercase">Dados não recuperados</h3>
             <p className="text-sm text-slate-500 mt-2 max-w-md">Ocorreu uma falha ao exibir os resultados da análise. Por favor, tente recomeçar o processo ou verifique se o áudio possui conteúdo audível.</p>
             <button onClick={() => setStep(1)} className="mt-8 px-5 sm:px-8 py-3 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-indigo-700 transition-all">Recomeçar Processo</button>
          </div>
        )
      )}
    </div>
  );
};

export default AIAnalysis;
